import { useEffect, useRef } from 'react';
import { useLatest } from 'react-use';

import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { SelectItemGroupLabel } from '@/components/form/baseInputs/SelectInput/SelectItemGroupLabel';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { visuallyHidden } from '@/styles/a11y';
import { COLORS } from '@/styles/tokens/colors';

import { isDiagramHexColor } from '../../diagramStyle';
import { DiagramColorSwatch } from './diagramStyleGlyphs';

export interface DiagramColorOption {
  /** The saved value, such as a palette token. */
  value: string;
  label: string;
  /** The CSS color of the swatch. */
  swatch: string;
}

export interface DiagramColorPickerProps {
  /** The menu heading. It is also the accessible name of the trigger. */
  label?: string;
  options: readonly DiagramColorOption[];
  /**
   * A value from `options`, or a `#RRGGBB` custom color. Null when the
   * selected elements have different colors.
   */
  value: string | null;
  onChange: (value: string) => void;
  /** When true, the menu has a Custom item that lets the user choose any hex color. */
  allowCustom?: boolean;
  disabled?: boolean;
}

/** The color that the native color input shows before the user chooses one. */
const DEFAULT_CUSTOM_COLOR = '#7c9cbf';

// Inset the end stops to keep red and blue visible inside the circular swatch.
const CUSTOM_COLOR_GRADIENT =
  'linear-gradient(136deg, #FF4D4D 15.25%, #F88C0A 32.6%, #E4DD7B 49.29%, #7AC787 66.31%, #4DC3FF 83.33%)';

/**
 * A toolbar button that opens a menu of color swatches. It has no diagram
 * state: the caller gives the value and applies the change.
 */
export function DiagramColorPicker({
  label = 'Color',
  options,
  value,
  onChange,
  allowCustom = false,
  disabled = false,
}: DiagramColorPickerProps) {
  const colorInputRef = useRef<HTMLInputElement>(null);
  const latestOnChange = useLatest(onChange);
  // React's `onChange` of a color input runs for each color while the user
  // drags in the native picker. The native `change` event runs once, for the
  // final color, so one choice is one undo step.
  useEffect(() => {
    const input = colorInputRef.current;
    if (!input) return;
    const applyColor = () => latestOnChange.current(input.value);
    input.addEventListener('change', applyColor);
    return () => input.removeEventListener('change', applyColor);
  }, [allowCustom, latestOnChange]);
  const current = options.find((option) => option.value === value);
  // A hex color that matches a menu color selects that color, not Custom.
  const customColor = !current && isDiagramHexColor(value) ? value : null;
  const triggerSwatch =
    current?.swatch ?? customColor ?? COLORS.PRIMITIVES.WHITE;

  return (
    <>
      <ButtonWithPopover
        variant="transparent"
        size="xs"
        disabled={disabled}
        label={<DiagramColorSwatch color={triggerSwatch} />}
        aria-label={label}
        tooltip={label}
        highlightWhenOpen
        popperProps={{
          anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
          transformOrigin: { horizontal: 'left', vertical: 'top' },
          PaperProps: { sx: { minWidth: 180 } },
        }}
      >
        <SelectItemGroupLabel label={label} />
        {options.map((option) => (
          <MenuItem
            key={option.value}
            icon={<DiagramColorSwatch color={option.swatch} />}
            label={option.label}
            selected={option.value === value}
            onClick={() => onChange(option.value)}
          />
        ))}
        {allowCustom && (
          <MenuItem
            icon={<DiagramColorSwatch color={CUSTOM_COLOR_GRADIENT} />}
            label="Custom"
            selected={!!customColor}
            onClick={() => {
              const input = colorInputRef.current;
              if (!input) return;
              input.value = customColor ?? DEFAULT_CUSTOM_COLOR;
              input.click();
            }}
          />
        )}
      </ButtonWithPopover>
      {allowCustom && (
        <input
          ref={colorInputRef}
          type="color"
          tabIndex={-1}
          aria-hidden
          style={visuallyHidden}
        />
      )}
    </>
  );
}
